 import { useEffect, useRef, useState } from 'react';
 import styles from './VideoIntroPopup.module.css';

 interface Props {
  onClose: () => void;
 }

 // Keys
 const LS_DISABLE_KEY = 'vagonla_video_intro_disabled';
 const SS_SHOWN_KEY = 'vagonla_video_intro_session_shown';

 const VideoIntroPopup: React.FC<Props> = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    try {
      const disabled = localStorage.getItem(LS_DISABLE_KEY);
      const shownThisSession = sessionStorage.getItem(SS_SHOWN_KEY);
      const globalShown = (window as any).__VAGONLA_VIDEO_SHOWN__;
      console.debug('[VideoIntroPopup] init disabled=%s shownSession=%s globalShown=%s', disabled, shownThisSession, globalShown);
      if (!disabled && !shownThisSession && !globalShown) {
        const t = setTimeout(() => {
          setIsVisible(true);
          try {
            sessionStorage.setItem(SS_SHOWN_KEY, 'true');
            (window as any).__VAGONLA_VIDEO_SHOWN__ = true;
          } catch {}
        }, 300);
        return () => clearTimeout(t);
      } else {
        console.debug('[VideoIntroPopup] skip show (flags)');
        onClose();
      }
    } catch (e) {
      console.warn('[VideoIntroPopup] init error', e);
      onClose();
    }
  }, [onClose]);

  // Fallback: 1s sonra hâlâ görünmüyorsa ve flag set edilmediyse direkt aç.
  useEffect(() => {
    if (isVisible) return;
    const f = setTimeout(() => {
  if (!isVisible && !(window as any).__VAGONLA_VIDEO_SHOWN__) {
        setIsVisible(true);
        try {
          sessionStorage.setItem(SS_SHOWN_KEY, 'true');
          (window as any).__VAGONLA_VIDEO_SHOWN__ = true;
        } catch {}
      }
    }, 1000);
    return () => clearTimeout(f);
  }, [isVisible]);

  const close = () => {
    setIsVisible(false);
    setTimeout(onClose, 20);
  };

  const handleDontShowAgain = () => {
    try { localStorage.setItem(LS_DISABLE_KEY, 'true'); } catch {}
    close();
  };

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.muted) {
      el.muted = false;
      setSoundOn(true);
      el.play().catch(()=>{});
    } else {
      el.muted = true;
      setSoundOn(false);
    }
  };

  const handleVideoClick = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.muted) {
      el.muted = false;
      setSoundOn(true);
      el.play().catch(()=>{});
    } else {
      el.muted = true;
      setSoundOn(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.popup}>
  <div className={styles.videoContainer}>
          <div className={styles.topButtons}>
            <button
              className={styles.floatingBtn}
              onClick={toggleSound}
              aria-label={soundOn ? 'Sesi Kapat' : 'Sesi Aç'}
              onMouseMove={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                e.currentTarget.style.setProperty('--mx', x + '%');
                e.currentTarget.style.setProperty('--my', y + '%');
              }}
              onMouseLeave={() => {
                // Do not reset --mx/--my so glow fades out at last cursor position
              }}
              onTouchStart={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.classList.add('touching');
                const touch = e.touches[0];
                const rect = el.getBoundingClientRect();
                const x = ((touch.clientX - rect.left) / rect.width) * 100;
                const y = ((touch.clientY - rect.top) / rect.height) * 100;
                el.style.setProperty('--mx', x + '%');
                el.style.setProperty('--my', y + '%');
              }}
              onTouchMove={(e) => {
                const el = e.currentTarget as HTMLElement;
                const touch = e.touches[0];
                const rect = el.getBoundingClientRect();
                const x = ((touch.clientX - rect.left) / rect.width) * 100;
                const y = ((touch.clientY - rect.top) / rect.height) * 100;
                el.style.setProperty('--mx', x + '%');
                el.style.setProperty('--my', y + '%');
              }}
              onTouchEnd={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.classList.remove('touching');
                // Ortaya yumuşak dönüş için timeout ile geciktir
                setTimeout(() => {
                  el.style.setProperty('--mx', '50%');
                  el.style.setProperty('--my', '50%');
                }, 120);
              }}
              onTouchCancel={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.classList.remove('touching');
                el.style.setProperty('--mx', '50%');
                el.style.setProperty('--my', '50%');
              }}
            >
              {soundOn ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 9 9 9 13 5 13 19 9 15 5 15 5 9" />
                    <path d="M17 9a3 3 0 0 1 0 6" />
                    <path d="M19.5 7a6 6 0 0 1 0 10" />
                  </svg>
                  <span>Ses Açık</span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 9 9 9 13 5 13 19 9 15 5 15 5 9" />
                    <line x1="19" y1="5" x2="19" y2="5" />
                    <line x1="16" y1="8" x2="19" y2="5" />
                    <line x1="19" y1="5" x2="22" y2="8" />
                    <line x1="22" y1="16" x2="19" y2="19" />
                    <line x1="19" y1="19" x2="16" y2="16" />
                  </svg>
                  <span>Ses Kapalı</span>
                </>
              )}
            </button>
          </div>
          {!videoError && (
            <video
              ref={videoRef}
              autoPlay
              muted={!soundOn}
              playsInline
              preload="auto"
              onLoadedData={() => {
                const v = videoRef.current; if (v) {
                  // Autoplay garantiye almak için manuel dene
                  v.play().catch(err => console.debug('[VideoIntroPopup] manual play fail', err));
                }
                console.debug('[VideoIntroPopup] video loaded');
              }}
              onCanPlay={() => {
                const v = videoRef.current; if (v && v.paused) {
                  v.play().catch(err => console.debug('[VideoIntroPopup] play on canplay fail', err));
                }
              }}
              onEnded={close}
              onClick={handleVideoClick}
              onError={() => {
                console.warn('[VideoIntroPopup] video load error: /vagonla_commercial_1.mp4');
                setVideoError(true);
              }}
              // Mutlaka kök public yolundan yükle; rota derinliklerinde relative kırılmasın
              src={'/vagonla_commercial_1.mp4'}
            />
          )}
          {videoError && (
            <div className={styles.fallbackArea}>
              <div className={styles.fallbackInner}>
                <p className={styles.fallbackTitle}>Tanıtım Videosu Yüklenemedi</p>
                <p className={styles.fallbackText}>Bağlantı ya da dosya sorunu olabilir. Aşağıdaki butonlarla devam edebilirsin.</p>
              </div>
            </div>
          )}
          <div className={styles.actions}>
          <button
            className={styles.skipButton}
            onClick={close}
            onMouseMove={(e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              e.currentTarget.style.setProperty('--mx', x + '%');
              e.currentTarget.style.setProperty('--my', y + '%');
            }}
            onMouseLeave={() => {
              // keep last position
            }}
            onTouchStart={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.classList.add('touching');
              const touch = e.touches[0];
              const rect = el.getBoundingClientRect();
              const x = ((touch.clientX - rect.left) / rect.width) * 100;
              const y = ((touch.clientY - rect.top) / rect.height) * 100;
              el.style.setProperty('--mx', x + '%');
              el.style.setProperty('--my', y + '%');
            }}
            onTouchMove={(e) => {
              const el = e.currentTarget as HTMLElement;
              const touch = e.touches[0];
              const rect = el.getBoundingClientRect();
              const x = ((touch.clientX - rect.left) / rect.width) * 100;
              const y = ((touch.clientY - rect.top) / rect.height) * 100;
              el.style.setProperty('--mx', x + '%');
              el.style.setProperty('--my', y + '%');
            }}
            onTouchEnd={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.classList.remove('touching');
              setTimeout(() => {
                el.style.setProperty('--mx', '50%');
                el.style.setProperty('--my', '50%');
              }, 120);
            }}
            onTouchCancel={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.classList.remove('touching');
              el.style.setProperty('--mx', '50%');
              el.style.setProperty('--my', '50%');
            }}
          >
            Geç
          </button>
            <button
              className={styles.dontShowButton}
              onClick={handleDontShowAgain}
              onMouseMove={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                e.currentTarget.style.setProperty('--mx', x + '%');
                e.currentTarget.style.setProperty('--my', y + '%');
              }}
              onMouseLeave={() => {
                // keep last position
              }}
              onTouchStart={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.classList.add('touching');
                const touch = e.touches[0];
                const rect = el.getBoundingClientRect();
                const x = ((touch.clientX - rect.left) / rect.width) * 100;
                const y = ((touch.clientY - rect.top) / rect.height) * 100;
                el.style.setProperty('--mx', x + '%');
                el.style.setProperty('--my', y + '%');
              }}
              onTouchMove={(e) => {
                const el = e.currentTarget as HTMLElement;
                const touch = e.touches[0];
                const rect = el.getBoundingClientRect();
                const x = ((touch.clientX - rect.left) / rect.width) * 100;
                const y = ((touch.clientY - rect.top) / rect.height) * 100;
                el.style.setProperty('--mx', x + '%');
                el.style.setProperty('--my', y + '%');
              }}
              onTouchEnd={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.classList.remove('touching');
              }}
              onTouchCancel={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.classList.remove('touching');
              }}
            >
              Bir Daha Gösterme
            </button>
          </div>
        </div>
      </div>
    </div>
  );
 };

 export default VideoIntroPopup;